import React, { useState } from "react";
import { getDownloadURL, ref } from "firebase/storage";
import { storage, auth } from "../firebase";
import { Button } from "../design/button";
import { ArrowDownTrayIcon, EyeIcon } from "@heroicons/react/16/solid";

interface FileActionButtonsProps {
  path: string;
  showView?: boolean;
  showDownload?: boolean;
}

/**
 * Component that provides View and Download buttons for files.
 * - View: Opens file in a new tab for in-browser preview (works great for PDFs, images, etc.)
 * - Download: Forces download of the file
 */
const FileActionButtons: React.FC<FileActionButtonsProps> = ({
  path,
  showView = true,
  showDownload = true,
}) => {
  const [isLoading, setIsLoading] = useState<"view" | "download" | null>(null);

  const getFileUrl = async (): Promise<string | null> => {
    try {
      // Force refresh token before accessing storage to ensure latest claims
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }
      const fileRef = ref(storage, path);
      return await getDownloadURL(fileRef);
    } catch (error: any) {
      console.error("File access error:", error);
      const errorCode = error?.code || "unknown";
      const errorMessage = error?.message || "Unknown error";

      if (errorCode === "storage/unauthorized") {
        alert("You don't have permission to access this file. Please try logging out and back in.");
      } else if (errorCode === "storage/object-not-found") {
        alert("The file was not found. It may have been deleted.");
      } else {
        alert(`Failed to access file: ${errorMessage}`);
      }
      return null;
    }
  };

  const handleView = async () => {
    setIsLoading("view");
    try {
      const url = await getFileUrl();
      if (url) {
        // Open in new tab for viewing
        window.open(url, "_blank");
      }
    } finally {
      setIsLoading(null);
    }
  };

  const handleDownload = async () => {
    setIsLoading("download");
    try {
      const url = await getFileUrl();
      if (url) {
        // Extract filename from path
        const fileName = path.split("/").pop() || "download";

        // Create a temporary link and trigger download
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.target = "_blank";

        // For cross-origin files, we need to fetch and create a blob
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          link.href = blobUrl;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        } catch {
          // Fallback: just open in new tab (browser will handle download)
          window.open(url, "_blank");
        }
      }
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {showView && (
        <Button
          plain
          onClick={handleView}
          disabled={isLoading !== null}
          title="View file"
        >
          <EyeIcon className={`w-4 h-4 ${isLoading === "view" ? "animate-pulse" : ""}`} />
          {isLoading === "view" ? "Opening..." : "View"}
        </Button>
      )}
      {showDownload && (
        <Button
          plain
          onClick={handleDownload}
          disabled={isLoading !== null}
          title="Download file"
        >
          <ArrowDownTrayIcon className={`w-4 h-4 ${isLoading === "download" ? "animate-pulse" : ""}`} />
          {isLoading === "download" ? "Downloading..." : "Download"}
        </Button>
      )}
    </div>
  );
};

export default FileActionButtons;
